import { useEffect, useState } from 'react'
import { Building, Scale, Phone, FileText, Plus, X, Loader2, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import DiscoveryItem from './DiscoveryItem'
import type { ContactInfo } from './types'

interface BuilderPhaseDiscoveryProps {
  scrapeStatus: string
  businessName: string
  practiceAreas: string[]
  businessDescription: string
  contactInfo: ContactInfo
  onUpdateName: (name: string) => void
  onUpdateAreas: (areas: string[]) => void
  onUpdateDescription: (desc: string) => void
  onContinue: () => void
}

export default function BuilderPhaseDiscovery({
  scrapeStatus,
  businessName,
  practiceAreas,
  businessDescription,
  contactInfo,
  onUpdateName,
  onUpdateAreas,
  onUpdateDescription,
  onContinue,
}: BuilderPhaseDiscoveryProps) {
  const [editingName, setEditingName] = useState(false)
  const [newArea, setNewArea] = useState('')

  const isLoading = scrapeStatus === 'scraping'
  // Sticky mode flag. Without this, the view swapped between manual-entry
  // and "data found" the instant the user typed the first letter (because
  // businessName became truthy), unmounting the input and dropping their
  // keystroke. Mode is set ONCE based on whether the scrape returned data,
  // and only changes when scrapeStatus changes — typing into manual entry
  // can't flip it.
  const initialHasData = !!(businessName || practiceAreas.length > 0 || businessDescription)
  const scrapeFinishedWithData = ['complete', 'preloaded'].includes(scrapeStatus) && initialHasData
  const [mode, setMode] = useState<'auto' | 'manual'>(scrapeFinishedWithData ? 'auto' : 'manual')

  useEffect(() => {
    if (['complete', 'preloaded'].includes(scrapeStatus)) {
      if (businessName || practiceAreas.length > 0 || businessDescription) {
        setMode('auto')
      }
    }
    // Intentionally only watching scrapeStatus — typing into manual entry
    // must not flip the mode out from under the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrapeStatus])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 py-6 justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-brand-royal" />
          <span className="text-sm text-muted-foreground">Analyzing your website...</span>
        </div>
        <div className="h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-brand-indigo to-brand-royal animate-pulse rounded-full w-2/3" />
        </div>
      </div>
    )
  }

  if (mode === 'manual') {
    return (
      <div className="text-center py-6 space-y-3">
        <p className="text-sm text-muted-foreground">No data found. Enter your firm details below.</p>
        <div className="space-y-3 text-left">
          <Input
            placeholder="Your firm name"
            value={businessName}
            onChange={(e) => onUpdateName(e.target.value)}
          />
          <Input
            placeholder="Add a practice area and press Enter"
            value={newArea}
            onChange={(e) => setNewArea(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newArea.trim()) {
                onUpdateAreas([...practiceAreas, newArea.trim()])
                setNewArea('')
              }
            }}
          />
          {practiceAreas.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {practiceAreas.map((area) => (
                <Badge key={area} variant="secondary" className="gap-1">
                  {area}
                  <button onClick={() => onUpdateAreas(practiceAreas.filter(a => a !== area))}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          <Button className="w-full" onClick={onContinue} disabled={!businessName.trim()}>
            Continue <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Here's what we found</p>

      <div className="space-y-4">
        {/* Firm name */}
        {businessName && (
          <DiscoveryItem icon={Building} label="Firm name" delay={0}>
            {editingName ? (
              <Input
                value={businessName}
                onChange={(e) => onUpdateName(e.target.value)}
                onBlur={() => setEditingName(false)}
                onKeyDown={(e) => e.key === 'Enter' && setEditingName(false)}
                autoFocus
                className="h-8 text-sm"
              />
            ) : (
              <button
                className="font-medium hover:text-brand-royal transition-colors text-left"
                onClick={() => setEditingName(true)}
                title="Click to edit"
              >
                {businessName}
              </button>
            )}
          </DiscoveryItem>
        )}

        {/* Practice areas */}
        {practiceAreas.length > 0 && (
          <DiscoveryItem icon={Scale} label={`${practiceAreas.length} practice area${practiceAreas.length !== 1 ? 's' : ''}`} delay={300}>
            <div className="flex flex-wrap gap-1.5">
              {practiceAreas.map((area) => (
                <Badge key={area} variant="secondary" className="gap-1 text-xs">
                  {area}
                  <button onClick={() => onUpdateAreas(practiceAreas.filter(a => a !== area))}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))}
              <button
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors"
                onClick={() => {
                  const area = prompt('Add a practice area:')
                  if (area?.trim()) onUpdateAreas([...practiceAreas, area.trim()])
                }}
              >
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
          </DiscoveryItem>
        )}

        {/* Contact info */}
        {(contactInfo.phone || contactInfo.email) && (
          <DiscoveryItem icon={Phone} label="Contact info" delay={600}>
            <div className="text-xs text-muted-foreground space-y-0.5">
              {contactInfo.phone && <p>{contactInfo.phone}</p>}
              {contactInfo.email && <p>{contactInfo.email}</p>}
              {contactInfo.address && <p>{contactInfo.address}</p>}
            </div>
          </DiscoveryItem>
        )}

        {/* Description */}
        <DiscoveryItem icon={FileText} label="About Your Firm" delay={900}>
          <Textarea
            className="text-xs min-h-[80px] resize-none"
            value={businessDescription}
            onChange={(e) => onUpdateDescription(e.target.value)}
            placeholder="Describe your firm's services, specialties, and what makes you different. This directly shapes how your AI agent talks to potential clients."
          />
          <p className="text-[11px] text-amber-600 mt-1.5 font-medium">
            Important: Your AI agent uses this description to answer questions about your firm. The more detail you provide, the better your agent represents you.
          </p>
        </DiscoveryItem>
      </div>

      <div className="pt-2">
        <Button className="w-full" onClick={onContinue}>
          Looks good — continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
